import { TrendingUp, Clock, FileCheck, DollarSign } from 'lucide-react';
import { useI18n } from '../lib/i18n';

/**
 * Dólares SIN centavos. `formatUsd` los muestra y en una cifra de titular son ruido: "$185,000.00"
 * se lee peor que "$185,000" y nadie va a discutir los centavos de un total de seis dígitos. En
 * las pantallas internas se siguen usando los centavos, que ahí sí importan.
 */
const usd = (cents: number): string =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`;

/**
 * EL REPORTE DE EFECTIVIDAD — el mismo bloque que ve el equipo adentro y el cliente afuera.
 *
 * Es UN componente y no dos a propósito. Si la pantalla interna y la pública se dibujaran por
 * separado, tarde o temprano una muestra un número que la otra no, y el día que un cliente
 * pregunte por la diferencia no vamos a tener respuesta. Lo único que cambia entre las dos es lo
 * que las envuelve: adentro hay administración de links, afuera no hay nada.
 *
 * LAS ADVERTENCIAS ESTÁN EN LA PÁGINA, no escondidas en un tooltip. Un cliente que ve "+15%" tiene
 * que poder leer ahí mismo que es la mediana y sobre cuántos casos. No es cautela legal: es que
 * un número sin su definición se discute una sola vez y se pierde.
 */

export type Efectividad = {
  desde: string;
  recuperado_cents: number;
  claims_con_aumento: number;
  mediana_por_claim_cents: number | null;
  aumento_mediano_pct: number | null;
  dias_primer_cheque_p50: number | null;
  dias_primer_cheque_p90: number | null;
  claims_con_cheque: number;
  claims_trabajados: number;
  serie: Array<{ periodo: string; recuperado_cents: number; claims: number }>;
  generado_at: string;
};

/** La cifra grande con su rótulo. Es lo primero que el ojo agarra, así que va sola y sin adornos. */
function Cifra({ icono, valor, rotulo, destacada }: {
  icono: React.ReactNode; valor: string; rotulo: string; destacada?: boolean;
}) {
  return (
    <div className="mc-cifra" style={{
      display: 'grid', gap: 4, padding: '14px 16px', borderRadius: 12,
      background: destacada ? 'var(--accent-soft)' : 'var(--surface-2)',
      minWidth: 160, flex: '1 1 160px',
    }}>
      <span style={{ color: destacada ? 'var(--accent)' : 'var(--muted)' }}>{icono}</span>
      <div style={{
        fontSize: destacada ? 'var(--fs-2xl)' : 'var(--fs-xl)', fontWeight: 'var(--fw-bold)',
        fontFamily: 'var(--font-mono)', lineHeight: 1.1,
        color: destacada ? 'var(--accent)' : 'var(--ink)',
      }}>{valor}</div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {rotulo}
      </div>
    </div>
  );
}

/** Barras por trimestre. Sin eje truncado: la altura es proporcional al monto y nada más. */
function BarrasTrimestre({ serie }: { serie: Efectividad['serie'] }) {
  const { t } = useI18n();
  const tope = Math.max(...serie.map((s) => s.recuperado_cents), 1);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {t('eff.byQuarter')}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 150, paddingTop: 18 }}>
        {serie.map((s) => (
          <div key={s.periodo} style={{ flex: 1, display: 'grid', justifyItems: 'center', gap: 4, alignContent: 'end', height: '100%' }}>
            <div style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-bold)' }}>
              {usd(s.recuperado_cents)}
            </div>
            <div className="mc-barra" title={`${s.claims} claims`} style={{
              width: '100%', maxWidth: 64, borderRadius: '6px 6px 0 0', background: 'var(--accent)',
              height: `${Math.max(4, (s.recuperado_cents / tope) * 100)}px`,
            }} />
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>{s.periodo}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function EffectivenessReport({ d, empresa }: { d: Efectividad; empresa?: string }) {
  const { t } = useI18n();
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>{t('eff.title')}</h1>
        <div className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}>
          {empresa ? `${empresa} · ` : ''}{t('eff.since', { d: d.desde })}
        </div>
      </div>

      {/* El titular: la plata. Va solo y en grande porque es el número que importa. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Cifra destacada icono={<DollarSign size={18} strokeWidth={1.75} />}
          valor={usd(d.recuperado_cents)} rotulo={t('eff.recovered')} />
        <Cifra icono={<FileCheck size={18} strokeWidth={1.75} />}
          valor={String(d.claims_con_aumento)} rotulo={t('eff.claimsRaised')} />
        <Cifra icono={<TrendingUp size={18} strokeWidth={1.75} />}
          valor={d.aumento_mediano_pct != null ? `+${d.aumento_mediano_pct}%` : '—'}
          rotulo={t('eff.medianIncrease')} />
        <Cifra icono={<Clock size={18} strokeWidth={1.75} />}
          valor={d.dias_primer_cheque_p50 != null ? t('eff.days', { n: String(d.dias_primer_cheque_p50) }) : '—'}
          rotulo={t('eff.toFirstCheck')} />
      </div>

      {d.serie.length > 0 && <BarrasTrimestre serie={d.serie} />}

      {/* Cómo se arma cada número. En la página, no en un tooltip. */}
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', lineHeight: 1.6, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
        {t('eff.method', {
          n: String(d.claims_con_cheque),
          desde: d.desde,
          mediana: d.mediana_por_claim_cents != null ? usd(d.mediana_por_claim_cents) : '—',
        })}
      </div>
    </div>
  );
}
